import { useQuery } from '@tanstack/react-query';
import {
  pharmacyFeaturesApi,
  pharmacyFeaturesQueryKey,
  type PharmacyFeatures,
  type PharmacyFeaturesResponse,
} from '../../services/pharmacyFeatures';
import type { ProductVerificationEvidenceMode } from '../../services/pharmacyFeatures';
import { usePharmacyStore } from '../../stores/pharmacyStore';

/**
 * Pharmacy-level feature flags (System Configuration → Pharmacy Order Routing
 * & modules). One query definition for every consumer, keyed by pharmacy.
 */
export function usePharmacyFeatures(pharmacyId: string | null | undefined) {
  return useQuery<PharmacyFeaturesResponse>({
    queryKey: pharmacyFeaturesQueryKey(pharmacyId ?? ''),
    queryFn: () => pharmacyFeaturesApi.get(pharmacyId as string),
    enabled: !!pharmacyId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * PRX-24: the Clinic / IV module is ON unless the pharmacy switched it off in
 * System Configuration. While the flags are still loading the module stays
 * visible — the default every existing pharmacy runs with — rather than
 * flashing out and back in.
 */
export function isClinicModuleEnabled(
  features: Pick<PharmacyFeatures, 'clinicModuleEnabled'> | undefined,
): boolean {
  return features?.clinicModuleEnabled !== false;
}

export interface ClinicModuleState {
  /** false only when the pharmacy explicitly switched the module off */
  enabled: boolean;
  /** true until the flags have arrived once — render a skeleton, not the module (PRX-44 decision 4) */
  loading: boolean;
}

/** Pure so the queue pages and the router gate can be tested without React Query. */
export function resolveClinicModule(
  data: PharmacyFeaturesResponse | undefined,
  isLoading: boolean,
): ClinicModuleState {
  return { enabled: isClinicModuleEnabled(data?.features), loading: isLoading && !data };
}

/**
 * PRX-44: the one hook every clinic / IV surface reads. `enabled` follows the
 * pharmacy setting; `loading` lets a surface show a skeleton instead of
 * flashing the module in and out while the flags load.
 */
export function useClinicModule(): ClinicModuleState {
  const pharmacyId = usePharmacyStore((s) => s.activePharmacy?.id ?? null);
  const { data, isLoading } = usePharmacyFeatures(pharmacyId);
  return resolveClinicModule(data, isLoading);
}

/**
 * PRX-51: the pharmacy's product-verification evidence mode. Pure so the
 * dialog's gate can be tested without React Query.
 *
 * Resolves to 'scan' while loading, on an error, on an absent key and on any
 * unrecognised value — the strictly-more-evidence direction and today's
 * behaviour. It never resolves to "no evidence needed".
 */
export function resolveProductVerificationEvidence(
  data: PharmacyFeaturesResponse | undefined,
  isLoading: boolean,
): ProductVerificationEvidenceMode {
  if (isLoading && !data) return 'scan';
  return data?.features?.productVerificationEvidence === 'photo' ? 'photo' : 'scan';
}

/** The hook every product-verification surface reads. */
export function useProductVerificationEvidence(
  pharmacyId: string | null | undefined,
): ProductVerificationEvidenceMode {
  const { data, isLoading } = usePharmacyFeatures(pharmacyId);
  return resolveProductVerificationEvidence(data, isLoading);
}

/**
 * PRX-16: the one-page Shipping flow is OFF unless the pharmacy switched it on
 * in System Configuration. The opposite default to the clinic module above,
 * and deliberately: this flag REPLACES Ready to Ship and Staging, so an absent
 * flag, a failed request or a junk value must all leave the two pages a
 * pharmacy already has exactly where they are.
 */
export function isSimpleShippingEnabled(
  features: Pick<PharmacyFeatures, 'simpleShippingEnabled'> | undefined,
): boolean {
  return features?.simpleShippingEnabled === true;
}

export interface SimpleShippingState {
  /** true only when the pharmacy explicitly switched the page on */
  enabled: boolean;
  /** true until the flags have arrived once — a route gate shows a spinner
   *  rather than bouncing the user off a page that is about to exist */
  loading: boolean;
}

/** Pure so the sidebar and the router gate can be tested without React Query. */
export function resolveSimpleShipping(
  data: PharmacyFeaturesResponse | undefined,
  isLoading: boolean,
): SimpleShippingState {
  return { enabled: isSimpleShippingEnabled(data?.features), loading: isLoading && !data };
}

/** The one hook every Shipping surface reads. */
export function useSimpleShipping(): SimpleShippingState {
  const pharmacyId = usePharmacyStore((s) => s.activePharmacy?.id ?? null);
  const { data, isLoading } = usePharmacyFeatures(pharmacyId);
  return resolveSimpleShipping(data, isLoading);
}
